import { apiClient } from '../utils/apiClient';
import { getSAProxyInvestorId, saProxyApiClient } from './saProxyAdapter';

/**
 * Journey-scoped re-edit flow (post-final-submission information unlock).
 *
 * Mirrors the backend's EditAccessDto. `enforced` reports whether the backend lock flag
 * (app.re-edit.enforce-lock) is on — the UI must only disable editing when BOTH
 * `enforced` is true and `editable` is false, otherwise it would impose a lock the API
 * is not applying yet and break the current (happy) flow.
 */
export interface EditAccessDto {
  journeyId?: string | null;
  finalSubmitted: boolean;
  editable: boolean;
  requestStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'COMPLETED' | null;
  canRaise: boolean;
  /** True once this journey's account has been opened — re-editing is closed for good. */
  accountOpened?: boolean;
  rejectionRemarks?: string | null;
  cycleCount?: number;
  enforced: boolean;
}

export interface EditRequestDescription {
  investorUniqueId: string;
  investorName: string;
  spName?: string | null;
  reason: string;
  status: string;
  raisedAt?: string;
}

class EditRequestService {
  private readonly base = '/api/clients';

  /** Authenticated: current lock/request state for a journey.
   *  SA proxy mode (2026-09-06): calling raw apiClient sent the AGENT's token to the
   *  investor endpoint → 400 "Investor not found" → editAccess null → the Final Submit
   *  tick and Re-Edit tab never appeared on the agent's view. Pick the client the way
   *  profile.service does; adaptPath rewrites this to the SA /edit-access endpoint. */
  async getEditAccess(journeyId?: string): Promise<EditAccessDto> {
    const client = getSAProxyInvestorId() !== null ? saProxyApiClient : apiClient;
    const r = await client.get<EditAccessDto>(`${this.base}/me/edit-access`, {
      params: journeyId ? { journeyId } : undefined,
    });
    return r.data;
  }

  /** Authenticated: raise a re-edit request for a journey.
   *  SA proxy mode (2026-09-07): same client pick as getEditAccess — the SA endpoint is
   *  gated on can_submit_forms; without it the backend refuses and the FE hides the button. */
  async raise(journeyId: string | undefined, reason: string): Promise<EditAccessDto> {
    const client = getSAProxyInvestorId() !== null ? saProxyApiClient : apiClient;
    const r = await client.post<EditAccessDto>(
      `${this.base}/me/edit-requests`,
      { reason },
      { params: journeyId ? { journeyId } : undefined },
    );
    return r.data;
  }

  // Token endpoints — whitelisted on the backend; used by the SP from the e-mail link.

  async describe(token: string): Promise<EditRequestDescription> {
    const r = await apiClient.get<EditRequestDescription>(`${this.base}/edit-requests/${token}`);
    return r.data;
  }

  async approve(token: string): Promise<{ message: string }> {
    const r = await apiClient.post<{ message: string }>(`${this.base}/edit-requests/${token}/approve`);
    return r.data;
  }

  async reject(token: string, remarks: string): Promise<{ message: string }> {
    const r = await apiClient.post<{ message: string }>(`${this.base}/edit-requests/${token}/reject`, { remarks });
    return r.data;
  }
}

export const editRequestService = new EditRequestService();
